/**
 * Sprint 24.15 — HireWizardTemplatePreview (step 2→3 bridge, template path).
 *
 * Full detail view of one template: persona, instructions excerpt, equipment
 * (required vs optional), starter tasks. "Customise & hire" advances to the
 * customise form. Per the Sprint 24.15 visual contract.
 */

import type { CSSProperties } from "react";
import { usePreviewAgentTemplate } from "../../hooks/use-agent-templates";
import { TemplateIcon, EquipmentStatusDot, EquipmentMeta, equipmentStatusMeta } from "./template-visuals";

export interface HireWizardTemplatePreviewProps {
  templateId: string;
  onCustomise: (templateId: string) => void;
  onBack: () => void;
}

export function HireWizardTemplatePreview({ templateId, onCustomise, onBack }: HireWizardTemplatePreviewProps) {
  const { data: template, isLoading, isError, refetch } = usePreviewAgentTemplate(templateId);

  if (isLoading) {
    return (
      <div style={body}>
        <div style={{ ...skel, width: "60%", height: 14 }} />
        <div style={{ ...skel, width: "100%", height: 80 }} />
        <div style={{ ...skel, width: "100%", height: 120 }} />
      </div>
    );
  }
  if (isError || !template) {
    return (
      <div style={body}>
        <div style={stateCard}>
          <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>Couldn't load this template.</span>
          <button type="button" onClick={() => void refetch()} style={linkBtn}>
            <i className="ti ti-refresh" aria-hidden="true" /> Retry
          </button>
          <button type="button" onClick={onBack} style={linkBtn}>
            Back to gallery
          </button>
        </div>
      </div>
    );
  }

  const instructionsExcerpt =
    template.instructions.length > 600 ? `${template.instructions.slice(0, 597)}…` : template.instructions;

  return (
    <div style={body}>
      <nav style={breadcrumb}>
        <button type="button" onClick={onBack} style={crumbLink}>
          Templates
        </button>
        <span style={crumbSep}>/</span>
        <span style={crumbMuted}>{template.category}</span>
        <span style={crumbSep}>/</span>
        <span style={crumbCurrent}>{template.name}</span>
      </nav>

      {/* Header */}
      <section style={{ display: "flex", gap: 14, paddingBottom: 16, ...divider }}>
        <TemplateIcon icon={template.icon} ramp={template.ramp} size={56} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h3 style={h3}>{template.name}</h3>
            <span style={firstPartyPill}>First-party</span>
          </div>
          <div style={sourceLine}>
            {template.authored_by} · v{template.version}
          </div>
          <p style={personaText}>{template.persona}</p>
        </div>
      </section>

      {/* Instructions excerpt */}
      <section style={{ paddingBottom: 16, ...divider }}>
        <div style={sectionLabel}>Instructions</div>
        <div style={instructionsPanel}>{instructionsExcerpt}</div>
      </section>

      {/* Equipment */}
      {template.default_equipment.length > 0 && (
        <section style={{ paddingBottom: 16, ...divider }}>
          <div style={sectionLabel}>Equipment</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {template.default_equipment.map((e) => {
              const meta = equipmentStatusMeta(e.availability_status, e.availability_note, e.kind);
              const comingSoon = e.availability_status === "coming-soon";
              return (
                <div key={e.kind} style={equipRow}>
                  <EquipmentStatusDot color={meta.dotColor} size={6} />
                  <i className="ti ti-plug-connected" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                  <EquipmentMeta name={e.kind} equipmentType={e.equipment_type} purpose={e.purpose} />
                  {comingSoon ? (
                    <span style={comingSoonBadge}>{e.availability_note ? `Coming ${e.availability_note}` : "Coming soon"}</span>
                  ) : (
                    <span style={e.required ? requiredBadge : optionalBadge}>{e.required ? "Required" : "Optional"}</span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Starter tasks */}
      {template.starter_priorities.length > 0 && (
        <section style={{ paddingBottom: 8 }}>
          <div style={sectionLabel}>Starter tasks</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {template.starter_priorities.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <span style={numberChip}>{i + 1}</span>
                <span style={{ fontSize: 13, lineHeight: 1.4, color: "var(--color-text-primary)" }}>{p}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer actions */}
      <div style={footer}>
        <button type="button" onClick={onBack} style={secondaryBtn}>
          Cancel
        </button>
        <button type="button" onClick={() => onCustomise(template.id)} style={primaryBtn}>
          Customise &amp; hire <i className="ti ti-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

const body: CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 20px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const divider: CSSProperties = { borderBottom: "0.5px solid var(--color-border-tertiary)" };

const breadcrumb: CSSProperties = { display: "flex", alignItems: "center", gap: 6, fontSize: 13 };
const crumbLink: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const crumbMuted: CSSProperties = { color: "var(--color-text-tertiary)" };
const crumbCurrent: CSSProperties = { color: "var(--color-text-primary)" };
const crumbSep: CSSProperties = { color: "var(--color-text-tertiary)" };

const h3: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 500, color: "var(--color-text-primary)" };
const firstPartyPill: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-info)",
  color: "var(--color-text-info)",
};
const sourceLine: CSSProperties = { fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 };
const personaText: CSSProperties = {
  margin: "10px 0 0",
  fontSize: 14,
  lineHeight: 1.55,
  color: "var(--color-text-primary)",
};

const sectionLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  marginBottom: 8,
};
const instructionsPanel: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "12px 14px",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  lineHeight: 1.65,
  color: "var(--color-text-primary)",
  whiteSpace: "pre-wrap",
};

const equipRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
};
const requiredBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-danger)",
  color: "var(--color-text-danger)",
};
const optionalBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
};
const comingSoonBadge: CSSProperties = {
  fontSize: 11,
  padding: "1px 7px",
  borderRadius: 999,
  background: "var(--color-background-warning)",
  color: "var(--color-text-warning)",
  whiteSpace: "nowrap",
};
const numberChip: CSSProperties = {
  flexShrink: 0,
  width: 18,
  height: 18,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 4,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  fontSize: 11,
  fontWeight: 500,
};

const footer: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  paddingTop: 12,
  borderTop: "0.5px solid var(--color-border-tertiary)",
  marginTop: "auto",
};
const secondaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-secondary)",
  background: "transparent",
  color: "var(--color-text-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const primaryBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const stateCard: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 10,
  padding: 32,
};
const linkBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  color: "var(--color-text-info)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const skel: CSSProperties = { background: "var(--color-background-tertiary)", borderRadius: 4 };
